
import { useTranslation } from "react-i18next";
import { ShoppingBag, Hand, User, Flame, ArrowRight, LucideIcon } from "lucide-react";

interface DetectionModel {
  model: string;
  description: string;
  idealFor: string;
  icon: LucideIcon;
}

interface ModelCardProps extends DetectionModel {
  detailsPath: string;
}

function ModelCard({ model, description, idealFor, icon: Icon, detailsPath }: ModelCardProps) {
  const { t } = useTranslation();
  return (
    <div className="group relative bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6 transition-all duration-300 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-lg dark:hover:shadow-slate-900/40 h-full flex flex-col backdrop-blur-sm hover:-translate-y-1.5 text-center sm:text-left">
      <div>
        <div 
          className="w-10 h-10 rounded-xl bg-[#002549] text-white flex items-center justify-center mb-6 
                 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg mx-auto sm:mx-0 "
        >
          <Icon className="h-6 w-6 transition-transform duration-300 group-hover:scale-110" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">{model}</h3>
        <p className="text-slate-600 dark:text-slate-300/90 mb-4">{description}</p>
      </div>
      <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-700/50 flex flex-col gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('models.idealFor')}: <span className="text-slate-700 dark:text-slate-200">{idealFor}</span></p>
        <a
          href={detailsPath}
          className="inline-flex items-center text-primary-700 dark:text-primary-300 font-medium hover:underline hover:text-primary-900 dark:hover:text-primary-100 transition-colors duration-200 mt-2 group"
        >
          {t('models.learnMore')}
          <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
        </a>
      </div>
    </div>
  );
}

export function Models() {
  const { t } = useTranslation();

  const detectionModels: DetectionModel[] = [
    {
      model: t('models.theftDetection.model'),
      description: t('models.theftDetection.description'),
      idealFor: t('models.theftDetection.idealFor'),
      icon: ShoppingBag
    },
    {
      model: t('models.violenceDetection.model'),
      description: t('models.violenceDetection.description'),
      idealFor: t('models.violenceDetection.idealFor'),
      icon: Hand
    },
    {
      model: t('models.fallDetection.model'),
      description: t('models.fallDetection.description'),
      idealFor: t('models.fallDetection.idealFor'),
      icon: User
    },
    {
      model: t('models.fireSmokeDetection.model'),
      description: t('models.fireSmokeDetection.description'),
      idealFor: t('models.fireSmokeDetection.idealFor'),
      icon: Flame
    }
  ];

  return (
    <div id="models" className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900/90 dark:to-slate-800/90">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 sm:text-4xl">
            {t('models.title')}
          </h2>
          <p className="mt-3 text-lg text-slate-600 dark:text-slate-300/90 max-w-3xl mx-auto">
            {t('models.subtitle')}
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {detectionModels.map((model, index) => {
            // Map models to their detail routes
            let detailsPath = "/";
            if (model.model.toLowerCase().includes("fire")) detailsPath = "/fire-model-details";
            else if (model.model.toLowerCase().includes("fall")) detailsPath = "/fall-model-details";
            else if (model.model.toLowerCase().includes("violence")) detailsPath = "/violence-model-details";
            else if (model.model.toLowerCase().includes("theft")) detailsPath = "/shoplift-model-details";
            return (
              <ModelCard
                key={index}
                model={model.model}
                description={model.description}
                idealFor={model.idealFor}
                icon={model.icon}
                detailsPath={detailsPath}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
